import React, { useCallback, useEffect, useRef, useState } from "react";
import type { Value } from "platejs";
import { Plate, PlateContent, usePlateEditor } from "platejs/react";

import { proposalPlugins } from "./plugins";
import { proposalNodeComponents } from "./nodeRenderers";
import type { BlockSelectionInfo, PageBackground, PlatePage } from "./types";
import { BlockRegenerateButton } from "@/features/revisr/components/editor/BlockRegenerateButton";
import type { PolicyFooterMeta } from "./types";

type PageCanvasProps = {
  page: PlatePage;
  isActive: boolean;
  readOnly: boolean;
  zoom: number;
  onValueChange: (pageId: string, value: Value) => void;
  onFocus: (pageId: string) => void;
  onEditorMount?: (pageId: string, editor: unknown) => void;
  onBlockSelect?: (info: BlockSelectionInfo | null) => void;
  onRegenerateClick?: () => void;
  isRegenerating?: boolean;
  /** "N of M" label rendered at the page bottom; null for cover/TOC pages. */
  pageNumberLabel?: string | null;
  footerMeta?: PolicyFooterMeta;
  /** Inline footer edits bubble up through this callback (partial merge). */
  onFooterChange?: (updates: Partial<PolicyFooterMeta>) => void;
};

const renderBackgroundCss = (bg: PageBackground): React.CSSProperties => {
  if (bg.type === "solid") {
    return { backgroundColor: bg.color ?? "#ffffff" };
  }
  if (bg.type === "gradient" && bg.gradient) {
    const { type, angle = 180, stops } = bg.gradient;
    const stopStr = stops.map((s) => `${s.color} ${s.position}%`).join(", ");
    if (type === "linear") {
      return { backgroundImage: `linear-gradient(${angle}deg, ${stopStr})` };
    }
    if (type === "radial") {
      const { centerX = 50, centerY = 50, shape = "circle" } = bg.gradient;
      return {
        backgroundImage: `radial-gradient(${shape} at ${centerX}% ${centerY}%, ${stopStr})`,
      };
    }
    if (type === "conic") {
      return { backgroundImage: `conic-gradient(from ${angle}deg, ${stopStr})` };
    }
  }
  return { backgroundColor: bg.color ?? "#ffffff" };
};

export const PageCanvas: React.FC<PageCanvasProps> = ({
  page,
  isActive,
  readOnly,
  zoom,
  onValueChange,
  onFocus,
  onEditorMount,
  onBlockSelect,
  onRegenerateClick,
  isRegenerating,
  pageNumberLabel,
  footerMeta,
  onFooterChange,
}) => {
  const editor = usePlateEditor({
    plugins: proposalPlugins,
    components: proposalNodeComponents,
    value: page.plateValue,
  });

  const containerRef = useRef<HTMLDivElement>(null);
  const [btnPosition, setBtnPosition] = useState<{ top: number; left?: number; right?: number } | null>(null);
  const [showBtn, setShowBtn] = useState(false);
  const selectionTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Footer is inline-editable on content pages when an editor is wired up
  // (onFooterChange present) and we're not in read-only preview mode.
  const footerEditable = !readOnly && !!onFooterChange;
  const footerInputClass =
    "w-full bg-transparent border-0 outline-none p-0 m-0 text-gray-700 cursor-text";
  const footerInputStyle: React.CSSProperties = {
    fontSize: 10 * zoom,
    lineHeight: 1.4,
    fontFamily: 'inherit',
  };

  useEffect(() => {
    onEditorMount?.(page.id, editor);
    return () => onEditorMount?.(page.id, null);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page.id]);

  const trackSelection = useCallback(() => {
    if (readOnly || !isActive) {
      setShowBtn(false);
      onBlockSelect?.(null);
      return;
    }

    if (selectionTimeoutRef.current) {
      clearTimeout(selectionTimeoutRef.current);
    }

    selectionTimeoutRef.current = setTimeout(() => {
      try {
        const sel = editor.selection;
        if (!sel) {
          setShowBtn(false);
          onBlockSelect?.(null);
          return;
        }

        const blockEntries = Array.from(
          editor.nodes({
            at: sel,
            mode: 'highest',
            match: (n: unknown) => editor.isBlock(n as Record<string, unknown>),
          }),
        );

        if (blockEntries.length === 0) {
          setShowBtn(false);
          onBlockSelect?.(null);
          return;
        }

        const [node, path] = blockEntries[0] as [Record<string, unknown>, number[]];
        const blockIndex = path[0];
        const blockType = (typeof node.type === 'string' ? node.type : 'p');

        onBlockSelect?.({
          pageId: page.id,
          blockIndex,
          blockType,
        });

        // Position the button near the selected block
        const domSelection = window.getSelection();
        if (domSelection && domSelection.rangeCount > 0 && containerRef.current) {
          const range = domSelection.getRangeAt(0);
          let targetEl: HTMLElement | null = range.startContainer as HTMLElement;
          if (targetEl.nodeType === Node.TEXT_NODE) {
            targetEl = targetEl.parentElement;
          }
          // Walk up to find the top-level Slate block element
          while (targetEl && containerRef.current.contains(targetEl)) {
            const slateNode = targetEl.getAttribute?.('data-slate-node');
            if (slateNode === 'element' && targetEl.parentElement?.getAttribute?.('data-slate-node') !== 'element') {
              break;
            }
            targetEl = targetEl.parentElement;
          }

          if (targetEl && containerRef.current.contains(targetEl)) {
            const containerRect = containerRef.current.getBoundingClientRect();
            const blockTop = targetEl.getBoundingClientRect().top;

            // Anchor the button to the section heading the clicked block
            // belongs to — i.e. the nearest heading at or above it — so it
            // always sits to the right of the section title, not the
            // individual paragraph the user clicked.
            const headings = Array.from(
              containerRef.current.querySelectorAll<HTMLElement>('h1, h2, h3, h4'),
            );
            let anchorEl: HTMLElement = targetEl;
            for (let i = headings.length - 1; i >= 0; i--) {
              if (headings[i].getBoundingClientRect().top <= blockTop + 1) {
                anchorEl = headings[i];
                break;
              }
            }

            const anchorRect = anchorEl.getBoundingClientRect();
            setBtnPosition({
              top: (anchorRect.top - containerRect.top) - 4,
              // Anchor the button's right edge to the heading's right edge so
              // it stays inside the page, not spilling past the paper border.
              right: containerRect.right - anchorRect.right,
            });
            setShowBtn(true);
          }
        }
      } catch {
        setShowBtn(false);
        onBlockSelect?.(null);
      }
    }, 300);
  }, [editor, isActive, readOnly, page.id, onBlockSelect]);

  useEffect(() => {
    return () => {
      if (selectionTimeoutRef.current) {
        clearTimeout(selectionTimeoutRef.current);
      }
    };
  }, []);

  const bgStyle = renderBackgroundCss(page.background);

  return (
    <div
      ref={containerRef}
      data-testid="plate-page"
      data-page-id={page.id}
      onMouseDown={() => onFocus(page.id)}
      style={{
        width: page.canvasSize.width * zoom,
        minHeight: page.canvasSize.height * zoom,
      }}
      className={`relative mx-auto my-6 shadow-sm rounded transition-shadow ${
        isActive
          ? "shadow-md outline outline-1 outline-emerald-400"
          : "outline outline-1 outline-gray-200"
      }`}
    >
      <div
        aria-hidden
        className="absolute inset-0 pointer-events-none"
        style={bgStyle}
      />
      <Plate
        editor={editor}
        readOnly={readOnly}
        onValueChange={({ value }) => {
          onValueChange(page.id, value);
          trackSelection();
        }}
      >
        <div
          className="relative"
          style={{
            padding: 56 * zoom,
            // Extra bottom room so content never collides with the footer bar.
            paddingBottom: (pageNumberLabel ? 120 : 56) * zoom,
            fontSize: 16 * zoom,
            fontFamily: 'inherit',
          }}
          onMouseUp={trackSelection}
          onKeyUp={trackSelection}
        >
          <PlateContent
            placeholder="Start writing..."
            className="outline-none focus:outline-none min-h-[400px]"
          />
        </div>
      </Plate>

      {/* Footer — inline-editable on every content page when not in preview;
          render-only chrome (static spans) in preview mode. Cover/TOC pages
          pass null pageNumberLabel and render nothing. */}
      {pageNumberLabel && (
        <div
          aria-hidden={!footerEditable || undefined}
          className={`absolute left-0 right-0 flex items-end justify-between border-t border-gray-200 ${
            footerEditable ? '' : 'pointer-events-none select-none'
          }`}
          style={{
            bottom: 0,
            height: 96 * zoom,
            padding: `${8 * zoom}px ${56 * zoom}px`,
            boxSizing: 'border-box',
          }}
        >
          <div
            className="flex flex-col gap-0.5 text-gray-700"
            style={{
              fontSize: 10 * zoom,
              lineHeight: 1.4,
              fontFamily: 'inherit',
              flex: 1,
              minWidth: 0,
            }}
          >
            {footerEditable ? (
              <>
                <div className="flex items-center gap-1">
                  <span className="shrink-0">©</span>
                  <input
                    type="text"
                    value={footerMeta?.date ?? ''}
                    onChange={(e) => onFooterChange?.({ date: e.target.value })}
                    placeholder="Date"
                    className={footerInputClass}
                    style={footerInputStyle}
                  />
                </div>
                <input
                  type="text"
                  value={footerMeta?.policyName ?? ''}
                  onChange={(e) => onFooterChange?.({ policyName: e.target.value })}
                  placeholder="Policy name"
                  className={footerInputClass}
                  style={footerInputStyle}
                />
                <input
                  type="text"
                  value={footerMeta?.orgName ?? ''}
                  onChange={(e) => onFooterChange?.({ orgName: e.target.value })}
                  placeholder="Organisation"
                  className={footerInputClass}
                  style={footerInputStyle}
                />
                <input
                  type="text"
                  value={footerMeta?.classification ?? ''}
                  onChange={(e) => onFooterChange?.({ classification: e.target.value })}
                  placeholder="Classification"
                  className={footerInputClass}
                  style={footerInputStyle}
                />
              </>
            ) : (
              <>
                {footerMeta?.date && <span>© {footerMeta.date}</span>}
                {footerMeta?.policyName && <span>{footerMeta.policyName}</span>}
                {footerMeta?.orgName && <span>{footerMeta.orgName}</span>}
                {footerMeta?.classification && <span>{footerMeta.classification}</span>}
              </>
            )}
          </div>
          <div
            className="ml-4 shrink-0 text-gray-700 whitespace-nowrap"
            style={{ fontSize: 10 * zoom, fontFamily: 'inherit' }}
          >
            Page {pageNumberLabel}
          </div>
        </div>
      )}

      {/* Floating regenerate button */}
      {!readOnly && isActive && onRegenerateClick && (
        <BlockRegenerateButton
          visible={showBtn}
          position={btnPosition ?? { top: 0, left: 0 }}
          onClick={onRegenerateClick}
          disabled={isRegenerating}
        />
      )}
    </div>
  );
};
